import React from 'react'
import { toast } from 'sonner'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { IconEditOutline } from '@icons/IconEditOutline'
import { Button } from '@/components/shadcn/button'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { WorkspaceUsersForm } from '../Forms/WorkspaceUsersForm'
import { Badge } from '@/components/shadcn/badge'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { clsx } from 'clsx'
import { getInitials } from '@/lib/utils'
import { IconGlobeOutline } from '@icons/IconGlobeOutline'

type MessagesProjectsProjectUser = RestSchemas['Messages.Projects.ProjectUser']

type DirectLinkedUserCardProps = {
  user: MessagesProjectsProjectUser
  editEnabled: boolean
  projectId: number
  workspaceId: number
}

export const DirectLinkedUserCardV2: React.FC<DirectLinkedUserCardProps> = ({
  user,
  editEnabled,
  projectId,
  workspaceId,
}) => {
  const { getWorkspacesApiGetUsers, deleteProjectsApiRemoveUserFromProject } = useRestClient()

  const invalidateProjectUsers = () => {
    getWorkspacesApiGetUsers.invalidateQueries({ parameters: { query: { workspaceId } } })
  }

  const { mutate: deleteUser /* isPending: deleteUserPending */ } = deleteProjectsApiRemoveUserFromProject.useMutation()

  const handleDeleteUser = (user: RestSchemas['Messages.Projects.ProjectUser']) => {
    const mappedRoles = user.memberRoles.map((role) => ({
      roleId: role.roleId,
      projectId: projectId || 0,
      userId: user.id,
    }))
    deleteUser(
      { body: mappedRoles },
      {
        onSuccess: () => {
          toast.success('User removed successfully.')
          // Invalidate the query to refresh the users list
          invalidateProjectUsers()
        },
        onError: () => {
          toast.error('Failed to remove user.')
        },
      }
    )
  }

  const titleDescription = (
    <div className="flex flex-col justify-between gap-1">
      <div className="text-base">
        {user.givenName} {user.familyName}
      </div>
      <div className="text-xs font-light">{user.email}</div>
    </div>
  )

  const footer = (
    <div className="col-span-2 col-start-2 flex flex-row justify-between gap-2 border-t pt-3">
      <div className="flex flex-wrap items-center gap-1">
        {user.memberRoles.map((role) => (
          <Badge key={role.roleId} variant="secondary" className="h-5">
            {role.roleName}
          </Badge>
        ))}
      </div>
      {editEnabled && (
        <WorkspaceUsersForm
          sheetTitle="Edit User Roles"
          sheetTriggerTooltip="Edit User Roles"
          sheetDescription="Edit the user roles."
          selectedUser={user}
          sheetTrigger={
            <Button variant="ghost" size="icon">
              <IconEditOutline />
              <span className="sr-only">Edit roles</span>
            </Button>
          }
        />
      )}
    </div>
  )

  return (
    // <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-background p-4 shadow">
    <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 bg-background p-4 shadow">
      <div className="relative inline-block">
        <Avatar className="size-12 ring-2 ring-background">
          <AvatarFallback className="bg-accent text-base text-accent-foreground">
            {getInitials(clsx(user.givenName, user.familyName))}
          </AvatarFallback>
        </Avatar>
        {(() => {
          const hasAuthCode =
            !!user &&
            typeof user === 'object' &&
            'authCode' in user &&
            typeof (user as { authCode?: unknown }).authCode === 'string' &&
            ((user as { authCode?: string }).authCode?.length ?? 0) > 0
          return hasAuthCode
        })() && (
          <span
            title="External user"
            className="absolute -top-1 right-2 inline-flex size-4 items-center justify-center rounded-full bg-emerald-600 ring-2 ring-background"
          >
            <IconGlobeOutline size={10} className="text-background" />
          </span>
        )}
      </div>
      {titleDescription}
      <ConfirmDialog
        triggerTooltip="Remove user"
        trigger={
          <Button className="justify-self-end" variant="ghost" size="icon">
            <IconDeleteOutline />
            <span className="sr-only">Remove user</span>
          </Button>
        }
        title="Remove user from workspace"
        description="Please confirm that the user should be removed from the project."
        onConfirm={() => handleDeleteUser(user)}
      />
      {footer}
    </div>
  )
}
